import { useEffect, useState } from "react";
import { AxiosError } from "axios";
import { useFindUser } from "@/data/user.ts";
import { useTranslation } from "react-i18next";
import { Navigate, useParams } from "react-router-dom";
import { FindUserResponse, ILanguages } from "@/types";
import routes from "@/config/routes.ts";
import AuthBar from "@edusogno/layouts/auth-bar";
import NotFound from "@/pages/NotFound.tsx";
import LinearProgress from "@/components/ui/LinearProgress";
import RegistrationForms from "@/components/auth/registration/Forms.tsx";
import RegistrationFinish from "@/components/auth/registration/Finish.tsx";

export interface IFinalStepData {
  selectedLanguage?: ILanguages;
  selectedStartMonth?: string;
}

const Registration = () => {
  const { t } = useTranslation();
  const { token } = useParams();

  const { user, error, isLoading } = useFindUser(token || "");

  const [step, setStep] = useState(0);
  const [disabled, setDisabled] = useState(true);
  const [parentStep, setParentStep] = useState(0);
  const [hasOneToOne, setHasOneToOne] = useState(false);
  const [dataFinalStep, setDataFinalStep] = useState<IFinalStepData>();

  useEffect(() => {
    if (!user) return;

    if (
      user &&
      user.item.payments[0]?.paid_for
        .toLowerCase()
        .includes("lezioni individuali")
    ) {
      setHasOneToOne(true);
    }
  }, [user]);

  if (isLoading) return <LinearProgress />;

  const axiosError = error as AxiosError;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  if (axiosError?.response.data?.message === "EDS_ERROR.USER_NOT_FOUND") {
    return <NotFound />;
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  if (axiosError?.response.data?.message === "EDS_ERROR.ACCOUNT_EXISTS") {
    return <Navigate to={routes.login} />;
  }

  return (
    <>
      <AuthBar
        onClick={() => {
          if (step !== 0) {
            if (parentStep !== 0) {
              return setParentStep((r) => r - 1);
            }
            setStep((r) => r - 1);
            setDisabled(false);
          }
        }}
        progress={
          parentStep === 0
            ? !hasOneToOne
              ? 0.167 * step * 95
              : 0.2 * step * 95
            : 100
        }
        showButton={step !== 0 && parentStep == 0}
        buttonLabel={t("btns.back")}
      />

      {parentStep === 0 ? (
        <RegistrationForms
          step={step}
          user={user?.item}
          setStep={setStep}
          disabled={disabled}
          nextStep={() => setParentStep(1)}
          setDisabled={setDisabled}
          hasOneToOne={hasOneToOne}
          setDataFinalStep={setDataFinalStep}
        />
      ) : (
        <RegistrationFinish
          user={user?.item as FindUserResponse}
          data={dataFinalStep as IFinalStepData}
          hasOneToOne={hasOneToOne}
        />
      )}
    </>
  );
};

export default Registration;
